/**
 * Injetado num <style> dentro da Shadow DOM — nunca vaza pro CSS do site do
 * cliente, e o CSS do site do cliente nunca vaza pra cá. Tokens vêm da
 * referência de design (Fin, da Intercom): painel quase monocromático
 * (preto/branco/creme), cor de marca do tenant só no launcher fechado.
 */
export function buildStyles(accentColor: string): string {
  return `
    :host {
      all: initial;
      --pw-accent: ${accentColor};
      --pw-bg: #f4f1ea;
      --pw-surface: #ffffff;
      --pw-ink: #1c1c1c;
      --pw-ink-inverse: #ffffff;
      --pw-border: rgba(28, 28, 28, 0.08);
      --pw-radius-panel: 20px;
      --pw-radius-bubble: 18px;
      --pw-shadow: 0 16px 40px rgba(0, 0, 0, 0.18), 0 2px 10px rgba(0, 0, 0, 0.08);
      /*
       * Custom property, não font-family direto: "all" não reseta custom
       * properties (exceção explícita da spec), então --pw-font sobrevive
       * garantido. Cada elemento com texto herda via var(--pw-font) no
       * seletor universal abaixo, em vez de depender só de herança através
       * de :host (que também deveria funcionar, mas isso é mais à prova de
       * navegador).
       */
      --pw-font: "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    * { box-sizing: border-box; font-family: var(--pw-font); }

    .pw-launcher {
      position: fixed;
      width: 56px;
      height: 56px;
      border-radius: 9999px;
      background: var(--pw-accent);
      box-shadow: var(--pw-shadow);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.15s ease;
      /* Valor máximo de z-index (2^31 - 1) — a Shadow DOM não cria stacking
         context próprio, então um z-index baixo aqui só vence outros
         elementos do widget, não os do site do cliente que o hospeda. */
      z-index: 2147483647;
    }
    .pw-launcher:hover { transform: scale(1.06); }
    .pw-launcher svg { width: 26px; height: 26px; }

    .pw-panel {
      position: fixed;
      width: 380px;
      max-width: calc(100vw - 40px);
      height: 600px;
      max-height: calc(100vh - 120px);
      background: var(--pw-bg);
      border-radius: var(--pw-radius-panel);
      box-shadow: var(--pw-shadow);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      opacity: 0;
      transform: translateY(12px) scale(0.97);
      pointer-events: none;
      transition: opacity 0.16s ease, transform 0.16s ease;
      z-index: 2147483647;
    }
    .pw-panel.pw-open {
      opacity: 1;
      transform: none;
      pointer-events: auto;
    }

    .pw-header {
      padding: 16px 18px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--pw-border);
      flex-shrink: 0;
    }
    .pw-title { font-size: 15px; font-weight: 600; color: var(--pw-ink); }
    .pw-close {
      width: 28px;
      height: 28px;
      border-radius: 9999px;
      border: none;
      background: transparent;
      color: var(--pw-ink);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      line-height: 1;
    }
    .pw-close:hover { background: rgba(0, 0, 0, 0.06); }

    .pw-messages {
      flex: 1;
      overflow-y: auto;
      padding: 16px 18px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .pw-bubble {
      max-width: 80%;
      padding: 10px 14px;
      border-radius: var(--pw-radius-bubble);
      font-size: 14px;
      line-height: 1.45;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .pw-bubble--assistant {
      align-self: flex-start;
      background: var(--pw-surface);
      color: var(--pw-ink);
      border-bottom-left-radius: 6px;
    }
    .pw-bubble--user {
      align-self: flex-end;
      background: var(--pw-ink);
      color: var(--pw-ink-inverse);
      border-bottom-right-radius: 6px;
    }
    .pw-bubble--error {
      align-self: flex-start;
      background: transparent;
      color: #8a3b2b;
      font-size: 13px;
    }
    .pw-bubble strong { font-weight: 600; }
    .pw-bubble a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }
    .pw-bubble hr {
      border: none;
      border-top: 1px solid currentColor;
      opacity: 0.12;
      margin: 8px 0;
    }

    .pw-typing { display: inline-flex; gap: 4px; align-items: center; padding: 2px 0; }
    .pw-typing span {
      width: 6px;
      height: 6px;
      border-radius: 9999px;
      background: var(--pw-ink);
      opacity: 0.35;
      animation: pw-blink 1.1s infinite ease-in-out;
    }
    .pw-typing span:nth-child(2) { animation-delay: 0.15s; }
    .pw-typing span:nth-child(3) { animation-delay: 0.3s; }
    @keyframes pw-blink {
      0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
      40% { opacity: 0.9; transform: scale(1); }
    }

    .pw-input-bar {
      padding: 14px 16px;
      display: flex;
      gap: 8px;
      align-items: center;
      flex-shrink: 0;
    }
    .pw-input {
      flex: 1;
      border: none;
      box-shadow: inset 0 0 0 1px var(--pw-border);
      background: var(--pw-surface);
      border-radius: 9999px;
      padding: 11px 16px;
      font-size: 14px;
      color: var(--pw-ink);
      outline: none;
    }
    .pw-input:focus { box-shadow: inset 0 0 0 1.5px var(--pw-ink); }
    .pw-input::placeholder { color: rgba(28, 28, 28, 0.4); }

    .pw-send {
      width: 36px;
      height: 36px;
      border-radius: 9999px;
      border: none;
      background: var(--pw-ink);
      color: var(--pw-ink-inverse);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: opacity 0.15s ease;
    }
    .pw-send:disabled { opacity: 0.35; cursor: default; }
    .pw-send svg { width: 16px; height: 16px; }

    @media (max-width: 480px) {
      .pw-panel {
        width: 100vw;
        height: 100vh;
        max-width: 100vw;
        max-height: 100vh;
        border-radius: 0;
        inset: 0;
      }
    }
  `
}
